import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import * as routerActions from 'connected-react-router'
import { useReducedMotion } from 'framer-motion'
import { parse } from 'querystringify'
import cn from 'clsx'

// Actions
import { actions as payActions } from 'modules/pay/actions'
import { actions as staticpagesActions } from 'modules/staticpages/actions'
import { actions as subscriptionActions } from 'modules/subscription/actions'

// Components
import { Spinner } from '@tracklib/kit'
import ChoosePlan from 'components/subscribe/chooseplan'
import Header from 'components/subscribe/header'
import SEO from 'components/base/seo'
import Signup from 'components/subscribe/signup'
import SubscribeConfirm from 'components/subscribe/subscribeconfirm'

// Hooks
import { useFeatureToggle } from 'modules/featuretoggles/hooks'
import { useIsAuthenticated } from 'modules/auth/hooks'
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useSavedPaymentMethod } from 'modules/pay/hooks'
import { useSignupCode } from 'modules/signup/hooks'
import { useSubscription, useIsDirectPayment } from 'modules/subscription/hooks'

// Types
import { Dispatch, State, TierOffer, TierPeriod, SubscriptionOrder } from 'types'

// Utils
import { isBrowser } from '@process'

import styles from './subscribe.css'

const PATH = 'subscribe'

export const loadInitialData = () => async (dispatch: Dispatch) => {
  return dispatch(staticpagesActions.loadPageByPath(PATH))
}

const Subscribe = () => {
  const dispatch = useDispatch()
  const { code: urlCode } = useParams() as { code: string }
  const hasSavedPaymentMethod = !!useSavedPaymentMethod()
  const isAuthenticated = useIsAuthenticated()
  const prefersReducedMotion = useReducedMotion()
  const subscriptionOrder = useSelector<State, SubscriptionOrder>(
    state => Object.values(state.entities.subscriptionOrders).filter(({ state: orderState }) => orderState === 'UNPAID')[0],
  )
  const profile = useProfile()
  const subscription = useSubscription()
  const hasNoSubscription = ['TERMINATED', 'FREE'].includes(subscription?.state)
  const search = useSelector<State, string>(state => state.router.location.search)
  const { period } = parse(search) as { pstatus: string; period: string }
  const selectedPeriodId = +period
  const anniversaryDiscountFeatureToggle = useFeatureToggle('anniversaryDiscount')

  // Signup code
  const { refcode, refcodevalid, reftitle, reftext, inviter, image, force_signup_code, redirect_to } = useSignupCode()
  const animateBackground = !prefersReducedMotion && !image

  // Requests
  const fetchTierOffersRequest = useRequest('FETCH_TIER_OFFERS', refcode)
  const fetchInfoForCodeRequest = useRequest('FETCH_INFO_FOR_CODE', refcode)
  const fetchStaticPageRequest = useRequest('FETCH_STATIC_PAGE', PATH)
  const fetchProfileRequest = useRequest('FETCH_PROFILE')
  const isLoading =
    !isBrowser ||
    fetchTierOffersRequest.isLoading ||
    fetchInfoForCodeRequest.isLoading ||
    fetchStaticPageRequest.isLoading ||
    fetchProfileRequest.isLoading

  // Offers
  const tierOffers = useSelector<State, Array<TierOffer>>(state => Object.values(state.subscription[refcode] || {}))
  const defaultOffer = tierOffers.find(({ is_default }) => is_default) || tierOffers[0]
  const defaultPeriod = defaultOffer?.tier.periods.sort((a, b) => a.months - b.months)[0]
  const selectedOffer = tierOffers.find(offer => offer.tier.periods.some(p => p.id === selectedPeriodId))
  const selectedPeriod = selectedOffer?.tier.periods.find(p => p.id === selectedPeriodId)
  const selectedOfferId = selectedOffer?.id
  const isDirectPayment = useIsDirectPayment(selectedOffer, selectedPeriod?.months)

  // Selection
  const [selectedMonths, setSelectedMonths] = useState<number | undefined>(defaultPeriod?.months)

  // Redirect to /inspiration if entering page with subscription or redirect to /onboard if completed subscription process
  useEffect(() => {
    if (isAuthenticated && fetchProfileRequest.isDone && profile.id !== 0 && !hasNoSubscription) {
      dispatch(routerActions.replace(redirect_to || (profile.onboarded ? '/inspiration' : '/onboard')))
    }
  }, [redirect_to, isAuthenticated, fetchProfileRequest.isDone, profile.id, hasNoSubscription, dispatch, profile.onboarded])

  // If on url with faulty code, redierct to /subscribe
  useEffect(() => {
    if (urlCode && refcode !== urlCode) {
      dispatch(routerActions.replace('/subscribe'))
    }
  }, [refcode, urlCode, dispatch])

  // Set default period
  useEffect(() => {
    if (!selectedMonths && defaultPeriod) {
      setSelectedMonths(defaultPeriod.months)
    }
  }, [selectedMonths, defaultOffer, defaultPeriod])

  // Fetch tier offers
  useEffect(() => {
    if (!fetchTierOffersRequest.isLoading && !fetchTierOffersRequest.isDone) {
      dispatch(subscriptionActions.fetchTierOffers(refcode))
    }
  }, [dispatch, refcode, fetchTierOffersRequest])

  // Create subscription order
  useEffect(() => {
    if (isAuthenticated && selectedOfferId && selectedPeriod && isDirectPayment) {
      dispatch(payActions.createSubscriptionOrder(selectedOfferId, selectedPeriod.id))
    }
  }, [isAuthenticated, selectedOfferId, selectedPeriod, isDirectPayment, dispatch])

  const renderContent = () => {
    if (isLoading) {
      return <Spinner delay={0} minHeight={300} className={styles.spinner} />
    }
    if (!isAuthenticated) {
      return <Signup refcode={refcode} refcodevalid={refcodevalid} showSignupCode={force_signup_code} />
    }
    if (!selectedOffer) {
      return (
        <ChoosePlan
          selectedOfferId={selectedOfferId}
          selectedMonths={selectedMonths}
          setSelectedMonths={setSelectedMonths}
          offers={tierOffers}
        />
      )
    }
    /*
    if (!hasSavedPaymentMethod) {
      return (
        <div className={styles.contentWrapper}>
          <ChangePaymentMethod title="Add payment method" hideRemoveButton />
        </div>
      )
    }
    */
    return (
      <div className={styles.contentWrapper}>
        <SubscribeConfirm offer={selectedOffer} period={selectedPeriod as TierPeriod} subscriptionOrder={subscriptionOrder} />
      </div>
    )
  }

  let title = inviter ? reftitle.replace('Your friend', inviter) : reftitle || 'Subscribe'
  let description = reftext

  const finalStep = !isLoading && isAuthenticated && selectedOffer && hasSavedPaymentMethod
  if (finalStep) {
    title = 'Billing Information'
    description = 'Just one more step to go and then you’re in.'
  }

  return (
    <>
      {anniversaryDiscountFeatureToggle ? (
        <>
          <img src="/static/images/aniversary-fallback-bg.png" alt="" className={styles.videoBgFallback} />
          <video autoPlay muted loop playsInline className={styles.videoBg}>
            <source src="/static/videos/anniversary-site-bg.mp4" type="video/mp4" />
          </video>
        </>
      ) : (
        <div className={cn(styles.bg, animateBackground && styles.bgSpin)} style={image ? { backgroundImage: `url("${image}")` } : {}}>
          {animateBackground && [1, 2, 3, 4, 5, 6].map(x => <div key={x} className={styles.row} />)}
        </div>
      )}
      <SEO title={`${title} | Tracklib`}>
        <body data-subscribe="true" data-theme="light" />
      </SEO>
      <div className={styles.content}>
        <Header title={title} description={description} image={image} />
      </div>
      {renderContent()}
    </>
  )
}

export default Subscribe
